import { formatCurrency, formatCurrencyShort } from 'utils';

import { accountLabel } from './billing-run-table';
import { SummaryCard } from './SummaryCard';
import { useBillingRunSummary } from './useBillingRunSummary';

const groupLabel = (count: number) => `${count} ${count === 1 ? 'group' : 'groups'}`;

type BillingRunSummaryCardsProps = {
    summary: ReturnType<typeof useBillingRunSummary>;
    showCashCoverage: boolean;
};

export const BillingRunSummaryCards = ({ summary, showCashCoverage }: BillingRunSummaryCardsProps) => (
    <div className="grid grid-cols-1 gap-4 pb-4 xl:grid-cols-3">
        <SummaryCard
            title="Fee Calculation"
            statLabel="Fee calculated"
            statValue={`${summary.feeCalculatedGroups}/${summary.totalGroups}`}
            rows={[
                {
                    label: 'Total calculated fee',
                    value: <span className="font-semibold">{formatCurrency(summary.totalCalculatedFee)}</span>,
                },
                {
                    label: 'Total billable NAV',
                    value: <span className="font-semibold">{formatCurrencyShort(summary.totalBillableNav)}</span>,
                },
            ]}
            caption={
                <p className="text-muted-foreground text-sm">Unassigned account count requires backend support.</p>
            }
        />

        {showCashCoverage && (
            <SummaryCard
                title="Cash Coverage"
                statLabel="Cash covers"
                statValue={summary.coveredGroups == null ? '—' : `${summary.coveredGroups}/${summary.totalGroups}`}
                rows={[
                    {
                        label: 'Cash encumbered',
                        value: (
                            <span className="font-semibold">
                                {summary.cashEncumbered == null ? '—' : formatCurrency(summary.cashEncumbered)}
                            </span>
                        ),
                    },
                    {
                        label: 'Short to cover',
                        value: (
                            <span className="font-semibold text-red-900">
                                {summary.shortToCover == null ? '—' : formatCurrency(summary.shortToCover)}
                            </span>
                        ),
                    },
                ]}
                caption={
                    summary.shortAccounts == null || summary.shortGroups == null ? (
                        <p className="text-sm text-gray-700">Cash coverage unavailable.</p>
                    ) : (
                        <p className="text-sm text-gray-700">
                            Short across {accountLabel(summary.shortAccounts)} in {groupLabel(summary.shortGroups)}.
                        </p>
                    )
                }
            />
        )}

        <SummaryCard
            title="Settlement"
            statLabel="Settled"
            statValue={`${summary.settledGroups}/${summary.totalGroups}`}
            rows={[
                {
                    label: 'Outstanding fees',
                    value: (
                        <span className="font-semibold text-red-900">{formatCurrency(summary.outstandingFees)}</span>
                    ),
                },
                {
                    label: 'Settled to date',
                    value: <span className="font-semibold">{formatCurrency(summary.settledToDate)}</span>,
                },
            ]}
            caption={
                <p className="text-sm text-gray-700">
                    {summary.unpaidAccounts} unpaid · {summary.partialAccounts} partial
                </p>
            }
        />
    </div>
);
